import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set page title (overrides Emergent platform default)
document.title = "Track Vacances";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const loginInProgress = useRef(false);

  const checkAuth = useCallback(async () => {
    // Skip if a login flow is currently in progress
    if (loginInProgress.current) return;

    // Skip auth check when on the Google OAuth callback route
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === '/auth/google' && params.get('code')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Only set user if login is not in progress (guard against late responses)
      if (!loginInProgress.current) {
        setUser(response.data);
      }
    } catch {
      if (!loginInProgress.current) {
        if (token) {
          sessionStorage.removeItem("token");
          setToken(null);
        }
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Check auth on mount and when token changes
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { username: email, password });
    const { access_token, user: userData } = response.data;
    sessionStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, []);

  const register = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/register`, { username: email, password });
    const { access_token, user: userData } = response.data;
    sessionStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, []);

  const loginWithGoogle = useCallback(async (code, redirectUri) => {
    loginInProgress.current = true;
    try {
      const response = await axios.post(
        `${API}/auth/google/callback`,
        { code, redirect_uri: redirectUri }
      );
      const { access_token, user: userData } = response.data;
      sessionStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      return userData;
    } finally {
      setTimeout(() => { loginInProgress.current = false; }, 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.warn("Logout request failed:", err.message);
    }
    sessionStorage.removeItem("token");
    setToken(null);
    setUser(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are stable module-level constants
  }, []);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    checkAuth,
    setUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// OAuth Callback Handler — handles Google OAuth redirect with ?code=
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (!code) {
        toast.error("Code d'autorisation manquant");
        navigate("/login", { replace: true });
        return;
      }

      // Verify CSRF state
      const savedState = sessionStorage.getItem("oauth_state");
      if (!state || state !== savedState) {
        toast.error("État OAuth invalide, veuillez réessayer");
        navigate("/login", { replace: true });
        return;
      }
      sessionStorage.removeItem("oauth_state");

      const redirectUri = window.location.origin + "/auth/google";

      try {
        await loginWithGoogle(code, redirectUri);
        navigate("/", { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Échec de la connexion Google";
        toast.error(detail);
        navigate("/login", { replace: true });
      }
    };

    handleCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount, hasProcessed ref guards re-execution
  }, [loginWithGoogle, navigate]);

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/70">Connexion en cours...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // If already authenticated, render immediately (even if loading hasn't finished)
  if (isAuthenticated) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return <Navigate to="/login" replace />;
};

// App Router
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const TOASTER_STYLE = {
  background: '#18181B',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#FAFAFA',
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-[#09090B]">
          <Toaster 
            position="top-right" 
            theme="dark"
            toastOptions={{ style: TOASTER_STYLE }}
          />
          <AppRouter />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
